import { useEffect, useState } from "react";
import {
    Table,
    Card,
    CardBody,
    Button,
    Badge,
    Spinner,
    Input,
    Label
} from "reactstrap";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { assignEnquiryApi, getAllEnquiriesCoordinatorApi } from "../../../api/apiUrlConst";


interface Enquiry {
  id: number;
  eid: string;
  name: string;
  email: string;
  phoneNumber: string;
  courseCategory: string;
  courseName: string;
  status: string;
  submittedAt: string;

  assignedTo?: {
    userId: string;
    name: string;
  } | null;
}

const getStatusColor = (status: string) => {
    switch (status) {
        case "New": return "secondary";
        case "Assigned": return "primary";
        case "Joined": return "success";
        case "InFollowUp": return "warning";
        case "NotInterested": return "danger";
        case "DND": return "dark";
        default: return "light";
    }
};

const AllEnquiries = () => {
    const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
    const [loading, setLoading] = useState(true);
    const { userId } = useAuth(); // assuming user.userId exists
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [filtered, setFiltered] = useState<Enquiry[]>([]);
    const [userFilter, setUserFilter] = useState("");
    const [coordinators, setCoordinators] = useState<string[]>([]);
    const [dateFilter, setDateFilter] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const clearFilters = () => {
  setSearch("");
  setStatusFilter("");
  setUserFilter("");
  setDateFilter("");
  setStartDate("");
  setEndDate("");
};

        
 // 🔍 Filtering Logic
useEffect(() => {
  let data = enquiries;

  const today = new Date();

  // STATUS FILTER
  if (statusFilter) {
    data = data.filter((e) => e.status === statusFilter);
  }

  // COORDINATOR FILTER
  if (userFilter) {
    data = data.filter((e) => e.assignedTo?.name === userFilter);
  }

  // SEARCH FILTER
  if (search) {
    const searchLower = search.toLowerCase();

    data = data.filter((e) =>
      e.name?.toLowerCase().includes(searchLower) ||
      e.eid?.toLowerCase().includes(searchLower) ||
      e.courseCategory?.toLowerCase().includes(searchLower) ||
      e.courseName?.toLowerCase().includes(searchLower)
    );
  }

  // DATE FILTER
  if (dateFilter) {
    data = data.filter((e) => {
      const enquiryDate = new Date(e.submittedAt);

      if (dateFilter === "today") {
        return enquiryDate.toDateString() === today.toDateString();
      }

      if (dateFilter === "thisWeek") {
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay());

        return enquiryDate >= start && enquiryDate <= today;
      }

      if (dateFilter === "lastWeek") {
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay() - 7);

        const end = new Date(today);
        end.setDate(today.getDate() - today.getDay() - 1);

        return enquiryDate >= start && enquiryDate <= end;
      }

      if (dateFilter === "thisMonth") {
        return (
          enquiryDate.getMonth() === today.getMonth() &&
          enquiryDate.getFullYear() === today.getFullYear()
        );
      }

      if (dateFilter === "lastMonth") {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);

        return (
          enquiryDate.getMonth() === lastMonth.getMonth() &&
          enquiryDate.getFullYear() === lastMonth.getFullYear()
        );
      }

      return true;
    });
  }

  // CUSTOM DATE RANGE
  if (startDate && endDate) {
    data = data.filter((e) => {
      const enquiryDate = new Date(e.submittedAt);
      return enquiryDate >= new Date(startDate) && enquiryDate <= new Date(endDate);
    });
  }

  setFiltered(data);
}, [search, statusFilter, userFilter, enquiries, dateFilter, startDate, endDate]);

useEffect(() => {
  const names = enquiries
    .filter((e) => e.assignedTo?.name)
    .map((e) => e.assignedTo!.name);

  const uniqueNames = Array.from(new Set(names));

  setCoordinators(uniqueNames);
}, [enquiries]);


    const loadData = async () => {
        try {
            const data = await getAllEnquiriesCoordinatorApi();
            setEnquiries(data);
            setFiltered(data);
        } catch {
            toast.error("Failed to load enquiries");
        } finally {
            setLoading(false);
        }
    };

    const handleAssignToMe = async (eid: string) => {
        try {
            if (!userId) {
                toast.error("User not authenticated");
                return;
            }

            await assignEnquiryApi(eid, userId);
            toast.success("Assigned to you");
            loadData();
        } catch {
            toast.error("Assignment failed");
        }
    };

 useEffect(() => {
  loadData();
    }, []);

    if (loading) return <Spinner className="mt-5" />;

    return (
        <div  style={{ background: "#f4f6fb", minHeight: "100vh" }}>

  {/* ===== HEADER ===== */}
  <div
    className="p-4 mb-4"
    style={{
      background: "linear-gradient(90deg, #374151, #093d92)",
      borderRadius: "16px",
      color: "#fff",
    }}
  >
    
<div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">

          {/* Left Side */}
          <div>
           <h3 className="fw-bold mb-1">All Enquiries</h3>
    <small>Manage and assign enquiries efficiently</small>
          </div>

          {/* Right Side Filters */}
          <div className="d-flex flex-wrap align-items-end gap-3 mt-3">

  {/* Search */}
  <div>
    <Input
      placeholder="Search enquiry..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      className="shadow-sm"
      style={{ width: "220px" }}
    />
  </div>

  {/* Status */}
  <div>
    <Input
      type="select"
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      className="shadow-sm"
      style={{ width: "160px" }}
    >
      <option value="">All Status</option>
      <option>New</option>
      <option>Assigned</option>
      <option>InFollowUp</option>
      <option>Joined</option>
      <option>NotInterested</option>
      <option>DND</option>
    </Input>
  </div>

  {/* Date Filter */}
  <div>
    <Input
      type="select"
      value={dateFilter}
      onChange={(e) => setDateFilter(e.target.value)}
      className="shadow-sm"
      style={{ width: "160px" }}
    >
      <option value="">All</option>
      <option value="today">Today</option>
      <option value="thisWeek">This Week</option>
      <option value="lastWeek">Last Week</option>
      <option value="thisMonth">This Month</option>
      <option value="lastMonth">Last Month</option>
    </Input>
  </div>

  {/* Start Date */}
  <div>
    <Input
      type="date"
      value={startDate}
      onChange={(e) => setStartDate(e.target.value)}
      className="shadow-sm"
      style={{ width: "160px" }}
    />
  </div>

  {/* End Date */}
  <div>
    <Input
      type="date"
      value={endDate}
      onChange={(e) => setEndDate(e.target.value)}
      className="shadow-sm"
      style={{ width: "160px" }}
    />
  </div>

  {/* Clear Button */}
  <div>
    <Button
      color="primary"
      className="mt-4"
      onClick={clearFilters}
    >
      Clear
    </Button>
  </div>

</div>
        </div>

    
  </div>

  {/* ===== TABLE CARD ===== */}
  <Card
    className="border-0 shadow-sm"
    style={{ borderRadius: "16px" }}
  >
    <CardBody>

      <Table
        responsive
        hover
        className="align-middle"
        style={{
          borderCollapse: "separate",
          borderSpacing: "0 8px",
        }}
      >
        <thead>
          <tr style={{ background: "#eef2f7" }}>
            <th>EID</th>
            <th>Name</th>
            <th>Phone No</th>
            <th>Course</th>
            <th>Status</th>
            <th>Coordinator</th>
            <th className="text-end">Action</th>
          </tr>
        </thead>

        <tbody>
          {filtered.map((e) => (
            <tr
              key={e.id}
              style={{
                background: "#fff",
                boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              }}
            >
              {/* EID */}
              <td className="fw-bold text-primary">
                {e.eid}
              </td>

              {/* Name */}
              <td >
                {e.name}
              </td>
              {/* Phone No */}
              <td >
                {e.phoneNumber}
              </td>

              {/* Course Name */}
              <td className="fw-semibold">
                {e.courseName}
              </td>

              {/* Status */}
              <td>
                <Badge
                  color={getStatusColor(e.status)}
                  className="px-3 py-2 rounded-pill"
                >
                  {e.status}
                </Badge>
              </td>

              {/* Coordinator */}
              <td>
                {e.assignedTo ? (
                  <Badge
                    color="info"
                    className="px-3 py-2 rounded-pill"
                  >
                    👤 {e.assignedTo.name}
                  </Badge>
                ) : (
                  <Badge
                    color="light"
                    className="px-3 py-2 rounded-pill border"
                  >
                    Unassigned
                  </Badge>
                )}
              </td>

              {/* Action */}
              <td className="text-end">
                {e.assignedTo?.userId === userId ? (
                  <Button
                    size="sm"
                    className="rounded-pill"
                    color="success"
                    disabled
                  >
                    ✔ Assigned To You
                  </Button>
                ) : e.assignedTo ? (
                  <Button
                    size="sm"
                    className="rounded-pill"
                    color="secondary"
                    disabled
                  >
                    Assigned
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="rounded-pill"
                    style={{
                      background:
                        "linear-gradient(90deg,#667eea,#764ba2)",
                      border: "none",
                    }}
                    onClick={() =>
                      handleAssignToMe(e.eid)
                    }
                  >
                    Assign To Me
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

    </CardBody>
  </Card>
</div>
    );
};

export default AllEnquiries;